import { z } from "zod";

/**
 * Ein Schema für alles: es beschreibt, was das Modell liefern darf, was die
 * Edge Function zurückgibt und was die Vorschau-Karte anzeigt. Läuft die Form
 * auseinander, fällt es hier auf und nicht erst auf dem Telefon.
 */

/** Unter diesem Wert fragt die App nach, statt einfach anzulegen. */
export const UNSICHER_UNTER = 0.7;

/** Die Einheiten, die es gibt. Steht so auch im Systemprompt. */
export const AI_UNITS = ["stk", "g", "kg", "ml", "l", "pkg", "dose", "glas", "flasche", "bund", "becher"] as const;

export const AiItemSchema = z.object({
  name: z.string().min(1).max(120),
  qty: z.number().positive().nullable().default(null),
  /**
   * Einheit und Kategorie kommen absichtlich als freier Text herein und werden
   * erst in der Nachbereitung gegen die erlaubten Werte geprüft. Als strenge
   * Aufzählung würde eine erfundene Einheit die ganze Antwort ungültig machen,
   * und dann verlöre man auch die neun Artikel, die stimmen.
   */
  unit: z.string().nullable().default(null),
  category: z.string().nullable().default(null),
  /** ID aus dem Familienkatalog, wenn das Modell sicher ist. */
  product_id: z.string().nullable().default(null),
  confidence: z.number().min(0).max(1).default(1),
  /** Sammelbegriffe wie "Gemüse": anlegen, aber nachfragen. */
  needs_clarification: z.boolean().default(false),
  alternatives: z.array(z.string()).max(3).default([]),
});

export const AiActionSchema = z.object({
  type: z.enum(["add", "check", "remove", "create_list"]),
  list_id: z.string().nullable().default(null),
  list_name: z.string().nullable().default(null),
  list_confidence: z.number().min(0).max(1).default(1),
  items: z.array(AiItemSchema).default([]),
});

export const AiResultSchema = z.object({
  actions: z.array(AiActionSchema).default([]),
  summary: z.string().default(""),
});

export type AiItem = z.infer<typeof AiItemSchema>;
export type AiAction = z.infer<typeof AiActionSchema>;
export type AiResult = z.infer<typeof AiResultSchema>;

// --- Bon ---------------------------------------------------------------------

export const ReceiptPositionSchema = z.object({
  name: z.string().min(1),
  qty: z.number().positive().nullable().default(null),
  unit_price_cents: z.number().int().nonnegative().nullable().default(null),
  price_cents: z.number().int().nonnegative().nullable().default(null),
});

export const ReceiptSchema = z.object({
  store_name: z.string().nullable().default(null),
  purchased_at: z.string().nullable().default(null),
  total_cents: z.number().int().nonnegative().nullable().default(null),
  positions: z.array(ReceiptPositionSchema).default([]),
});

export type ReceiptPosition = z.infer<typeof ReceiptPositionSchema>;
export type Receipt = z.infer<typeof ReceiptSchema>;

/**
 * Nimmt entgegen, was das Modell geschickt hat, und macht daraus entweder ein
 * gültiges Ergebnis oder eine Fehlermeldung. Nie ein halbes Ergebnis: eine
 * Vorschau mit kaputten Feldern ist schlimmer als gar keine.
 */
export function parseAiResult(raw: unknown): { ok: true; value: AiResult } | { ok: false; error: string } {
  const parsed = AiResultSchema.safeParse(raw);
  return parsed.success ? { ok: true, value: parsed.data } : { ok: false, error: parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join(", ") };
}

export function parseReceipt(raw: unknown): { ok: true; value: Receipt } | { ok: false; error: string } {
  const parsed = ReceiptSchema.safeParse(raw);
  return parsed.success ? { ok: true, value: parsed.data } : { ok: false, error: parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join(", ") };
}

// --- Rezept ------------------------------------------------------------------

export const RecipeIngredientDraftSchema = z.object({
  name: z.string().min(1).max(120),
  qty: z.number().positive().nullable().default(null),
  /** Wie oben: freier Text, geprüft wird erst in der Nachbereitung. */
  unit: z.string().nullable().default(null),
  category: z.string().nullable().default(null),
  staple: z.boolean().default(false),
});

export const RecipeDraftSchema = z.object({
  name: z.string().default(""),
  servings: z.number().int().min(1).max(50).default(2),
  servings_guessed: z.boolean().default(false),
  note: z.string().nullable().default(null),
  ingredients: z.array(RecipeIngredientDraftSchema).default([]),
});

export type RecipeIngredientDraft = z.infer<typeof RecipeIngredientDraftSchema>;
export type RecipeDraft = z.infer<typeof RecipeDraftSchema>;

export function parseRecipeDraft(raw: unknown): { ok: true; value: RecipeDraft } | { ok: false; error: string } {
  const parsed = RecipeDraftSchema.safeParse(raw);
  return parsed.success ? { ok: true, value: parsed.data } : { ok: false, error: parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join(", ") };
}
